import {
  addMoney,
  divideMoney,
  multiplyMoney,
  serializeMoney,
} from '@erp/shared-utils';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { isBuyDesignated, type DesignatedLine } from './lineFilters';

/**
 * Purchase price variance for one unit: the buyer (system) cost minus what the
 * line buys at (its revised or contract cost, as priced on the buy leg).
 * Positive (favourable) when the buy is cheaper, negative when it costs more.
 * Null when either cost is unknown.
 */
export function ppvDeltaPerUnit(
  line: Pick<AssignmentLine, 'buyerUnitCost' | 'topOption'>,
): number | null {
  const unitCost = line.topOption?.unitCost ?? null;
  if (unitCost === null || line.buyerUnitCost === null) return null;
  // Costs carry four decimals; round away float noise at that scale.
  return Math.round((line.buyerUnitCost - unitCost) * 10_000) / 10_000;
}

/**
 * A typed revised cost: a number of at least zero, or null for a cleared
 * input. Undefined when the text is not a cost, so the edit is dropped.
 */
export function parseRevisedCost(text: string): number | null | undefined {
  const trimmed = text.trim().replace(/^\$/, '').replaceAll(',', '');
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) && value >= 0 ? value : undefined;
}

/**
 * A buyer's revised-cost action on one line or a group (GEN-3141): a
 * percentage off or on the buyer (system) cost, one set cost, or a reset back
 * to the system cost.
 */
export type RevisedCostAdjustment =
  | { kind: 'decrease'; percent: number }
  | { kind: 'increase'; percent: number }
  | { kind: 'set'; unitCost: number }
  | { kind: 'reset' };

/**
 * A line's revised cost after an adjustment. A percentage applies to the
 * buyer (system) cost, never to an earlier revised cost, so applying it twice
 * gives the same cost. Its result is rounded to the cent, half to even
 * (12.345 → 12.34, 12.355 → 12.36). A set cost is kept as typed. Undefined
 * when a percentage has no system cost to apply to.
 */
export function adjustedRevisedCost(
  buyerUnitCost: number | null,
  adjustment: RevisedCostAdjustment,
): number | null | undefined {
  switch (adjustment.kind) {
    case 'reset':
      return null;
    case 'set':
      return adjustment.unitCost;
    case 'decrease':
    case 'increase': {
      if (buyerUnitCost === null) return undefined;
      const sign = adjustment.kind === 'decrease' ? -1 : 1;
      const factor = addMoney(1, divideMoney(sign * adjustment.percent, 100));
      const cost = Number(
        serializeMoney(multiplyMoney(buyerUnitCost, factor), 2),
      );
      return Math.max(cost, 0);
    }
  }
}

export interface RevisedCostPlan {
  costs: { suggestionId: string; revisedUnitCost: number | null }[];
  /** Buy lines a percentage could not apply to: no system cost. */
  skipped: number;
}

/** The revised cost each buy line gets from an adjustment. */
export function planRevisedCosts(
  lines: readonly (DesignatedLine &
    Pick<AssignmentLine, 'suggestionId' | 'buyerUnitCost'>)[],
  adjustment: RevisedCostAdjustment,
): RevisedCostPlan {
  const plan: RevisedCostPlan = { costs: [], skipped: 0 };
  for (const line of lines) {
    if (!isBuyDesignated(line)) continue;
    const revisedUnitCost = adjustedRevisedCost(line.buyerUnitCost, adjustment);
    if (revisedUnitCost === undefined) plan.skipped += 1;
    else plan.costs.push({ suggestionId: line.suggestionId, revisedUnitCost });
  }
  return plan;
}

/**
 * A typed percentage: more than zero, and at most 100 for a decrease (a
 * cost cannot go below zero). Undefined when the text is not one.
 */
export function parseAdjustPercent(
  text: string,
  kind: 'decrease' | 'increase',
): number | undefined {
  const value = Number(text.trim().replace(/%$/, ''));
  if (!Number.isFinite(value) || value <= 0) return undefined;
  if (kind === 'decrease' && value > 100) return undefined;
  return value;
}
